// Property listing-requests endpoint functions.
//
// Endpoints accept camelCase view-model inputs and return camelCase view-model
// outputs. Action endpoints return 204 (no body) — the caller re-fetches the
// detail/list via query invalidation (§1). The success envelope
// (`{ success, statusCode, message, data }`) is unwrapped here; the list's
// `data` is `{ rows, meta }`.

import { adminApi } from '@core/api/httpClient'
import { LISTING_REQUESTS_ENDPOINT_PATHS } from './listingRequestsEndpointPaths'
import type {
  ApproveListingRequestPayloadDto,
  FinalApprovalResponseDto,
  LinkListingOwnerPayloadDto,
  ListingRequestDocumentDownloadDto,
  ListingRequestDto,
  ListingRequestEventDto,
  ListingRequestFeeQuoteDto,
  ListingRequestsListResponseDto,
  ListingRequestStatusCountsDto,
  ProposeListingPricePayloadDto,
  SetFundingWindowPayloadDto,
  RejectListingRequestPayloadDto,
  RequestListingRevisionPayloadDto,
  VerifyListingOwnerPayloadDto,
} from './listingRequestsContracts'
import type {
  ApproveListingRequestInput,
  FinalApprovalResult,
  FinalApproveListingRequestInput,
  GetListingRequestsParams,
  LinkListingOwnerInput,
  ListingRequest,
  ListingRequestDocumentDownload,
  ListingRequestDocumentDownloadInput,
  ListingRequestEvent,
  ListingRequestFeeQuote,
  ListingRequestsListResponse,
  ListingRequestStatusCounts,
  OnboardListingOwnerInput,
  ProposeListingPriceInput,
  ReissueAgreementInput,
  SetFundingWindowInput,
  RejectListingRequestInput,
  RequestListingRevisionInput,
  ResendOwnerInviteInput,
  RevokeOwnerInviteInput,
  StartAgreementSigningInput,
  UnlinkListingOwnerInput,
  UnverifyListingOwnerInput,
  VerifyListingOwnerInput,
} from '../types/listingRequestsTypes'
import {
  parseFinalApprovalResponse,
  parseListingRequest,
  parseListingRequestDocumentDownload,
  parseListingRequestFeeQuote,
  parseListingRequestsListResponse,
  parseListingRequestStatusCounts,
  parseListingRequestTimeline,
} from './listingRequestsParser'

type Enveloped<T> = T | { data: T }

function unwrap<T>(payload: Enveloped<T>): T {
  if (payload && typeof payload === 'object' && 'data' in payload) {
    return (payload as { data: T }).data
  }
  return payload as T
}

export async function getListingRequests(
  params: GetListingRequestsParams,
): Promise<ListingRequestsListResponse> {
  const queryParams: Record<string, string> = {}
  if (params.page) queryParams.page = String(params.page)
  if (params.limit) queryParams.limit = String(params.limit)
  // A multi-status queue sends `status=a,b`; the BE's `toArrayParam` transform
  // splits it. An empty array means "no status filter" (the All Requests view).
  if (Array.isArray(params.status)) {
    if (params.status.length) queryParams.status = params.status.join(',')
  } else if (params.status) {
    queryParams.status = params.status
  }
  if (params.sellerId) queryParams.sellerId = params.sellerId
  if (params.search) queryParams.search = params.search
  if (params.submittedFrom) queryParams.submittedFrom = params.submittedFrom
  if (params.submittedTo) queryParams.submittedTo = params.submittedTo

  const { data } = await adminApi.get<Enveloped<ListingRequestsListResponseDto>>(
    LISTING_REQUESTS_ENDPOINT_PATHS.LIST,
    { params: queryParams },
  )
  return parseListingRequestsListResponse(unwrap(data))
}

export async function getListingRequestStatusCounts(): Promise<ListingRequestStatusCounts> {
  const { data } = await adminApi.get<Enveloped<ListingRequestStatusCountsDto>>(
    LISTING_REQUESTS_ENDPOINT_PATHS.STATUS_COUNTS,
  )
  return parseListingRequestStatusCounts(unwrap(data))
}

export async function getListingRequest(id: string): Promise<ListingRequest> {
  const { data } = await adminApi.get<Enveloped<ListingRequestDto>>(
    LISTING_REQUESTS_ENDPOINT_PATHS.DETAIL(id),
  )
  return parseListingRequest(unwrap(data))
}

export async function getListingRequestFeeQuote(id: string): Promise<ListingRequestFeeQuote> {
  const { data } = await adminApi.get<Enveloped<ListingRequestFeeQuoteDto>>(
    LISTING_REQUESTS_ENDPOINT_PATHS.FEE_QUOTE(id),
  )
  return parseListingRequestFeeQuote(unwrap(data))
}

export async function getListingRequestTimeline(id: string): Promise<ListingRequestEvent[]> {
  const { data } = await adminApi.get<Enveloped<ListingRequestEventDto[]>>(
    LISTING_REQUESTS_ENDPOINT_PATHS.TIMELINE(id),
  )
  return parseListingRequestTimeline(unwrap(data))
}

export async function approveListingRequest(input: ApproveListingRequestInput): Promise<void> {
  const payload: ApproveListingRequestPayloadDto = {}
  if (input.decisionNotes) payload.decisionNotes = input.decisionNotes
  await adminApi.patch(LISTING_REQUESTS_ENDPOINT_PATHS.APPROVE(input.id), payload)
}

export async function rejectListingRequest(input: RejectListingRequestInput): Promise<void> {
  const payload: RejectListingRequestPayloadDto = { reason: input.reason }
  await adminApi.patch(LISTING_REQUESTS_ENDPOINT_PATHS.REJECT(input.id), payload)
}

export async function requestListingRevision(input: RequestListingRevisionInput): Promise<void> {
  const payload: RequestListingRevisionPayloadDto = { revisionNotes: input.revisionNotes }
  await adminApi.patch(LISTING_REQUESTS_ENDPOINT_PATHS.REQUEST_REVISION(input.id), payload)
}

export async function setFundingWindow(input: SetFundingWindowInput): Promise<void> {
  const payload: SetFundingWindowPayloadDto = { fundingWindowDays: input.fundingWindowDays }
  await adminApi.patch(LISTING_REQUESTS_ENDPOINT_PATHS.SET_FUNDING_WINDOW(input.id), payload)
}

export async function proposeListingPrice(input: ProposeListingPriceInput): Promise<void> {
  const payload: ProposeListingPricePayloadDto = { proposedPrice: input.proposedPrice }
  if (input.note) payload.note = input.note
  await adminApi.patch(LISTING_REQUESTS_ENDPOINT_PATHS.PROPOSE_PRICE(input.id), payload)
}

// ─── Owner verification (review stage) ───────────────────────────────────────

export async function verifyListingOwner(input: VerifyListingOwnerInput): Promise<void> {
  const payload: VerifyListingOwnerPayloadDto = {}
  if (input.note) payload.note = input.note
  await adminApi.patch(LISTING_REQUESTS_ENDPOINT_PATHS.OWNER_VERIFY(input.id, input.index), payload)
}

/**
 * Withdraw one owner's verification — for a row ticked in error.
 *
 * Not the same as sending the request back for revision: that resets EVERY
 * owner's verification and puts the request in the seller's hands. This clears
 * exactly one, and the reviewer carries on.
 */
export async function unverifyListingOwner(input: UnverifyListingOwnerInput): Promise<void> {
  await adminApi.delete(LISTING_REQUESTS_ENDPOINT_PATHS.OWNER_VERIFY(input.id, input.index))
}

// ─── Owner onboarding ────────────────────────────────────────────────────────

// Approving the request already linked or created every declared owner and sent
// their invites — the per-owner calls below are the exception path for the owners
// that bootstrap could not resolve, never the normal route.

export async function onboardListingOwner(input: OnboardListingOwnerInput): Promise<void> {
  await adminApi.post(LISTING_REQUESTS_ENDPOINT_PATHS.ONBOARD_OWNER(input.id, input.index))
}

export async function linkListingOwner(input: LinkListingOwnerInput): Promise<void> {
  const payload: LinkListingOwnerPayloadDto = { propertyOwnerId: input.propertyOwnerId }
  await adminApi.patch(LISTING_REQUESTS_ENDPOINT_PATHS.OWNER_LINK(input.id, input.index), payload)
}

export async function unlinkListingOwner(input: UnlinkListingOwnerInput): Promise<void> {
  await adminApi.delete(LISTING_REQUESTS_ENDPOINT_PATHS.OWNER_LINK(input.id, input.index))
}

/**
 * Mints a fresh onboarding link for one declared owner and emails it, killing
 * whatever link they held before.
 *
 * Takes no body — which owner, which request, and the token itself all come from
 * the server. The raw token is never returned through HTTP (plan §5.2), so there
 * is nothing for the frontend to display or copy: the email is the only delivery
 * channel, and `sentAt` on the next detail read is how an admin confirms it went.
 */
export async function resendOwnerInvite(input: ResendOwnerInviteInput): Promise<void> {
  await adminApi.post(LISTING_REQUESTS_ENDPOINT_PATHS.RESEND_OWNER_INVITE(input.id, input.index))
}

/** Kills the owner's live link without issuing a replacement, and with it any
 * onboarding session already opened from it. */
export async function revokeOwnerInvite(input: RevokeOwnerInviteInput): Promise<void> {
  await adminApi.delete(LISTING_REQUESTS_ENDPOINT_PATHS.OWNER_INVITE(input.id, input.index))
}

// ─── Stage moves after onboarding ────────────────────────────────────────────

/** Closes owner onboarding. The server refuses unless every owner is ACTIVE. */
export async function startAgreementSigning(input: StartAgreementSigningInput): Promise<void> {
  await adminApi.patch(LISTING_REQUESTS_ENDPOINT_PATHS.START_AGREEMENT_SIGNING(input.id))
}

/**
 * Reopens the agreement stage after an owner declined (or an admin rejected) an
 * agreement. The declined document stays on file; the next generate or upload
 * takes the next attempt number.
 */
export async function reissueAgreement(input: ReissueAgreementInput): Promise<void> {
  await adminApi.patch(LISTING_REQUESTS_ENDPOINT_PATHS.REISSUE_AGREEMENT(input.id))
}

/**
 * The last step of the lifecycle: re-checks the owners and the ownership split,
 * creates the draft property with its attachments and co-owner set, and flips
 * the request to `promoted` ("Approved for property setup").
 *
 * **Takes no body**: everything comes from the request row or platform
 * configuration. Safe to retry: an approval that failed after creating the
 * property is finished by calling it again, never creating a second one.
 */
export async function finalApproveListingRequest(
  input: FinalApproveListingRequestInput,
): Promise<FinalApprovalResult> {
  const { data } = await adminApi.post<Enveloped<FinalApprovalResponseDto>>(
    LISTING_REQUESTS_ENDPOINT_PATHS.FINAL_APPROVE(input.id),
  )
  return parseFinalApprovalResponse(unwrap(data))
}

/**
 * Mint a short-lived signed URL for one of the request's intake attachments.
 *
 * A GET that is nonetheless driven by a mutation hook, not a query: the response
 * expires, so caching it would hand the operator a dead link on the second click.
 */
export async function getListingRequestDocumentDownloadUrl(
  input: ListingRequestDocumentDownloadInput,
): Promise<ListingRequestDocumentDownload> {
  const { data } = await adminApi.get<Enveloped<ListingRequestDocumentDownloadDto>>(
    LISTING_REQUESTS_ENDPOINT_PATHS.DOCUMENT_DOWNLOAD_URL(input.id, input.documentType),
  )
  return parseListingRequestDocumentDownload(unwrap(data))
}
